import {FC, useState, useRef, useEffect, KeyboardEvent, FormEvent, ChangeEvent} from 'react'
import {KTSVG, ID, UserAvatar} from '../../../../../../../../../_metronic/helpers'
import {useMentions, User} from '../hooks/useMentions'
import {uploadCommentImage} from '../core/_requests'
import {OverlayTrigger, Tooltip, Modal} from 'react-bootstrap'
import clsx from 'clsx'
import './TaskComments.css'

type RichCommentEditorProps = {
  value: string
  onChange: (value: string) => void
  onAttachmentsChange?: (attachments: string[]) => void
  onSubmit: () => void
  placeholder?: string
  taskId: ID
  disabled?: boolean
}

export const RichCommentEditor: FC<RichCommentEditorProps> = ({
  value,
  onChange,
  onAttachmentsChange,
  onSubmit,
  placeholder = 'Adicione um comentário',
  taskId,
  disabled = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const savedSelectionRef = useRef<Range | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [showToolbar, setShowToolbar] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false)
  const [mentionPosition, setMentionPosition] = useState({top: 0, left: 0})
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0)
  const [uploadingImages, setUploadingImages] = useState<string[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [uploadedImagePaths, setUploadedImagePaths] = useState<string[]>([])
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')

  const {users: mentionUsers, loading: mentionLoading} = useMentions(mentionQuery)

  // Handle keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Handle mention navigation FIRST (before submit check)
    if (showMentionSuggestions && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedMentionIndex((prev) => 
          prev < mentionUsers.length - 1 ? prev + 1 : prev
        )
        return
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0))
        return
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(mentionUsers[selectedMentionIndex])
        return
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      } else if (e.key === ' ') {
        // Space key - close suggestions
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
    }

    // Handle Enter to submit (without Shift) - only if no mention suggestions are open
    if (e.key === 'Enter' && !e.shiftKey && !showMentionSuggestions) {
      e.preventDefault()
      if (value.trim() && !disabled) {
        onSubmit()
      }
      return
    }

    // Handle formatting shortcuts
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') {
        e.preventDefault()
        formatText('bold')
      } else if (e.key === 'i') {
        e.preventDefault()
        formatText('italic')
      }
    }
  }

  // Handle input changes and detect @ mentions
  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    // Use innerHTML to preserve images and formatting
    const html = e.currentTarget.innerHTML || ''
    onChange(html)

    // Check for @ mention
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      setShowMentionSuggestions(false)
      setMentionQuery('')
      return
    }

    const range = selection.getRangeAt(0)
    
    // First, check if cursor is inside a mention element - if so, close suggestions
    let currentNode: Node | null = range.startContainer
    let isInsideMention = false
    
    while (currentNode && currentNode !== e.currentTarget) {
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const element = currentNode as Element
        if (element.tagName === 'MENTION') {
          isInsideMention = true
          break
        }
      }
      currentNode = currentNode.parentNode
    }

    // If inside a mention, close suggestions
    if (isInsideMention) {
      setShowMentionSuggestions(false)
      setMentionQuery('')
      return
    }
    
    // Get HTML before cursor to verify the @ is in plain text, not in a mention tag
    const tempRange = range.cloneRange()
    tempRange.selectNodeContents(e.currentTarget)
    tempRange.setEnd(range.endContainer, range.endOffset)
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(tempRange.cloneContents())
    const htmlBeforeCursor = tempDiv.innerHTML
    
    // Check if there's a closing mention tag before cursor
    const lastMentionCloseIndex = htmlBeforeCursor.lastIndexOf('</mention>')
    
    let query = ''
    let shouldShowSuggestions = false
    
    if (lastMentionCloseIndex !== -1) {
      // There's a mention tag closed before cursor
      // Get everything after the last </mention>
      const afterLastMention = htmlBeforeCursor.substring(lastMentionCloseIndex + '</mention>'.length)
      // Remove HTML tags to get plain text after the mention
      const textAfterLastMention = afterLastMention.replace(/<[^>]*>/g, '')
      
      // Check if there's a @ in the text after the last mention
      const lastAtInAfterMention = textAfterLastMention.lastIndexOf('@')
      
      if (lastAtInAfterMention === -1) {
        // No @ found in text after mention, so the @ we found must be in a mention tag
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
      
      // Get the text from the last @ in textAfterLastMention to the end
      const textFromLastAt = textAfterLastMention.substring(lastAtInAfterMention)
      const afterMentionMatch = textFromLastAt.match(/@([\w]*)$/)
      
      // If we found a match in text after mention, it's a new mention
      if (!afterMentionMatch) {
        // No valid @ pattern found in text after mention
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
      
      // Use the query from text after mention (this is the correct query)
      query = afterMentionMatch[1].trim()
      shouldShowSuggestions = true
    } else {
      // No mention tags closed before cursor, but check if we're inside an open mention tag
      const openMentionTags = (htmlBeforeCursor.match(/<mention[^>]*>/gi) || []).length
      const closeMentionTags = (htmlBeforeCursor.match(/<\/mention>/gi) || []).length
      
      if (openMentionTags > closeMentionTags) {
        // We're inside an open mention tag
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
      
      // No mention tags, get query from text before cursor
      const textContent = e.currentTarget.innerText || ''
      const textBeforeCursor = textContent.substring(0, range.startOffset)
      
      // Check if the last character before cursor is a space - if so, close suggestions
      if (textBeforeCursor.length > 0 && textBeforeCursor[textBeforeCursor.length - 1] === ' ') {
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
      
      // Match @ followed by optional word characters (including empty for just "@")
      const mentionMatch = textBeforeCursor.match(/@([\w]*)$/)
      
      if (!mentionMatch) {
        // No @ pattern found, close suggestions
        setShowMentionSuggestions(false)
        setMentionQuery('')
        return
      }
      
      query = mentionMatch[1].trim()
      shouldShowSuggestions = true
    }
    
    // Show suggestions with the correct query
    if (shouldShowSuggestions) {
      setMentionQuery(query)
      setShowMentionSuggestions(true)
      
      // Position mention suggestions near cursor
      const rect = range.getBoundingClientRect()
      const editorRect = editorRef.current?.getBoundingClientRect()
      if (editorRect) {
        setMentionPosition({
          top: rect.bottom - editorRect.top + 5,
          left: rect.left - editorRect.left,
        })
      }
      setSelectedMentionIndex(0)
    }
  }

  // Insert mention into editor
  const insertMention = (user: User) => {
    if (!editorRef.current) return

    // Close suggestions immediately to prevent reopening
    setShowMentionSuggestions(false)
    const currentQuery = mentionQuery // Save current query before clearing
    setMentionQuery('')

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      editorRef.current.focus()
      return
    }

    const range = selection.getRangeAt(0)
    
    // Build the mention text pattern: @ + query
    const mentionText = `@${currentQuery}`
    
    // Get HTML before cursor to check for mention tags
    const tempRange = range.cloneRange()
    tempRange.selectNodeContents(editorRef.current)
    tempRange.setEnd(range.endContainer, range.endOffset)
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(tempRange.cloneContents())
    const htmlBeforeCursor = tempDiv.innerHTML
    
    // Check if there's a closing mention tag before cursor
    const lastMentionCloseIndex = htmlBeforeCursor.lastIndexOf('</mention>')
    
    // Find the @ in the DOM by searching backwards from cursor
    let currentRange = range.cloneRange()
    let found = false
    let attempts = 0
    const maxAttempts = 100 // Safety limit
    
    // Try to find the @ by searching backwards in text nodes
    while (!found && attempts < maxAttempts) {
      attempts++
      
      if (currentRange.startContainer.nodeType === Node.TEXT_NODE) {
        const textNode = currentRange.startContainer as Text
        const nodeText = textNode.textContent || ''
        const startOffset = currentRange.startOffset
        
        // Check if @ is in this node at or before startOffset
        const textBeforeAt = nodeText.substring(0, startOffset)
        const lastAtIndex = textBeforeAt.lastIndexOf('@')
        
        if (lastAtIndex !== -1) {
          // Found @ in this node, check if it matches our pattern
          const textFromAt = nodeText.substring(lastAtIndex)
          const match = textFromAt.match(/@([\w]*)$/)
          
          if (match) {
            // Check if this @ is in a mention tag (if there are mention tags before)
            let isInMentionTag = false
            if (lastMentionCloseIndex !== -1) {
              // Check if this text node is inside a mention element
              let parent: Node | null = textNode.parentNode
              while (parent && parent !== editorRef.current) {
                if (parent.nodeType === Node.ELEMENT_NODE) {
                  const element = parent as Element
                  if (element.tagName === 'MENTION') {
                    isInMentionTag = true
                    break
                  }
                }
                parent = parent.parentNode
              }
            }
            
            // If not in a mention tag, or if there are no mention tags before, use this @
            if (!isInMentionTag) {
              // Verify the query matches (allowing partial matches as user types)
              const queryMatch = match[1]
              if (queryMatch === currentQuery || 
                  currentQuery.startsWith(queryMatch) || 
                  queryMatch.startsWith(currentQuery) ||
                  currentQuery === '') {
                // Found the correct @
                currentRange.setStart(textNode, lastAtIndex)
                currentRange.setEnd(range.endContainer, range.endOffset)
                found = true
                break
              }
            }
          }
        }
      }
      
      // Move backwards to previous text node
      let moved = false
      if (currentRange.startContainer.previousSibling) {
        const prevSibling = currentRange.startContainer.previousSibling
        if (prevSibling.nodeType === Node.TEXT_NODE) {
          currentRange.setStart(prevSibling, (prevSibling as Text).textContent?.length || 0)
          currentRange.setEnd(range.endContainer, range.endOffset)
          moved = true
        } else {
          // Try to find last text node in previous sibling
          const walker = document.createTreeWalker(
            prevSibling,
            NodeFilter.SHOW_TEXT,
            null
          )
          let lastTextNode: Node | null = null
          let node: Node | null
          while ((node = walker.nextNode())) {
            lastTextNode = node
          }
          if (lastTextNode && lastTextNode.nodeType === Node.TEXT_NODE) {
            currentRange.setStart(lastTextNode, (lastTextNode as Text).textContent?.length || 0)
            currentRange.setEnd(range.endContainer, range.endOffset)
            moved = true
          }
        }
      }
      
      if (!moved) {
        // Try parent node
        if (currentRange.startContainer.parentNode && 
            currentRange.startContainer.parentNode !== editorRef.current) {
          const parent = currentRange.startContainer.parentNode
          if (parent.previousSibling) {
            currentRange.setStartBefore(parent.previousSibling)
            currentRange.setEnd(range.endContainer, range.endOffset)
          } else {
            break
          }
        } else {
          break
        }
      }
    }
    
    // If we didn't find it, use a simpler fallback
    if (!found) {
      // Fallback: use the current range and try to delete backwards by query length + 1 (@)
      currentRange = range.cloneRange()
      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const textNode = range.startContainer as Text
        const deleteLength = mentionText.length
        const newStart = Math.max(0, range.startOffset - deleteLength)
        currentRange.setStart(textNode, newStart)
        currentRange.setEnd(range.endContainer, range.endOffset)
      }
    }
    
    // Delete the @query text
    currentRange.deleteContents()

    // Create mention element (using <mention> tag as expected by backend)
    const mentionElement = document.createElement('mention')
    mentionElement.setAttribute('data-user-id', user.id.toString())
    mentionElement.setAttribute('data-user-name', user.name)
    mentionElement.contentEditable = 'false'
    mentionElement.textContent = `@${user.name}`

    // Insert the mention
    currentRange.insertNode(mentionElement)
    
    // Add a space after mention for better UX
    const spaceNode = document.createTextNode(' ')
    currentRange.setStartAfter(mentionElement)
    currentRange.insertNode(spaceNode)
    
    // Move cursor after space
    currentRange.setStartAfter(spaceNode)
    currentRange.collapse(true)
    selection.removeAllRanges()
    selection.addRange(currentRange)

    // Update value
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }

    // Ensure focus is restored and suggestions stay closed
    setTimeout(() => {
      editorRef.current?.focus()
      // Double-check that suggestions are closed
      setShowMentionSuggestions(false)
      setMentionQuery('')
    }, 0)
  }

  // Format text (bold, italic)
  const formatText = (format: 'bold' | 'italic') => {
    document.execCommand(format, false)
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
    editorRef.current?.focus()
  }

  // Open link modal
  const openLinkModal = () => {
    const selection = window.getSelection()
    let selectedText = ''
    
    // Save the current selection range
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      savedSelectionRef.current = range.cloneRange()
      selectedText = selection.toString()
    } else if (editorRef.current) {
      // If no selection, create a range at the end of editor
      const range = document.createRange()
      range.selectNodeContents(editorRef.current)
      range.collapse(false) // Collapse to end
      savedSelectionRef.current = range
    }
    
    setLinkText(selectedText)
    setLinkUrl('')
    setShowLinkModal(true)
  }

  // Insert link from modal
  const insertLinkFromModal = () => {
    if (!linkUrl.trim()) {
      alert('Por favor, informe a URL do link')
      return
    }

    if (!editorRef.current) {
      return
    }

    // Validate URL format
    let url = linkUrl.trim()
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:')) {
      url = 'https://' + url
    }

    // Restore saved selection or use current selection
    const selection = window.getSelection()
    let range: Range | null = null

    if (savedSelectionRef.current) {
      // Use saved selection
      range = savedSelectionRef.current.cloneRange()
    } else if (selection && selection.rangeCount > 0) {
      // Fallback to current selection
      range = selection.getRangeAt(0)
    } else {
      // Create range at end of editor
      range = document.createRange()
      range.selectNodeContents(editorRef.current)
      range.collapse(false)
    }

    if (range) {
      // Ensure range is within editor
      if (!editorRef.current.contains(range.commonAncestorContainer)) {
        range = document.createRange()
        range.selectNodeContents(editorRef.current)
        range.collapse(false)
      }

      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      
      const textToUse = linkText.trim() || url
      link.textContent = textToUse
      
      // Check if there's selected text to replace
      const selectedText = range.toString()
      if (selectedText) {
        // Replace selected text with link
        range.deleteContents()
        range.insertNode(link)
      } else {
        // Insert link at cursor position
        range.insertNode(link)
      }

      // Move cursor after the link
      const newRange = document.createRange()
      newRange.setStartAfter(link)
      newRange.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(newRange)

      if (editorRef.current) {
        onChange(editorRef.current.innerHTML)
      }
    }

    setShowLinkModal(false)
    setLinkUrl('')
    setLinkText('')
    savedSelectionRef.current = null
    
    // Focus editor after a short delay to ensure modal is closed
    setTimeout(() => {
      editorRef.current?.focus()
    }, 100)
  }

  // Close link modal
  const closeLinkModal = () => {
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkText('')
    editorRef.current?.focus()
  }

  // Handle image upload
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    if (fileArray.length > 3) {
      alert('Máximo de 3 imagens por comentário')
      return
    }

    for (const file of fileArray) {
      if (file.size > 3 * 1024 * 1024) {
        alert(`A imagem ${file.name} não pode ser maior que 3MB`)
        continue
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        const preview = e.target?.result as string
        setImagePreviews((prev) => [...prev, preview])
      }
      reader.readAsDataURL(file)

      // Upload image
      setUploadingImages((prev) => [...prev, file.name])
      try {
        const response = await uploadCommentImage(file, taskId)
        const imagePath = response.path || `tasks/${file.name}`
        // Use the URL from backend, or construct it properly
        const imageUrl = response.url || `${process.env.REACT_APP_THEME_API_URL}/storage/${imagePath}`

        // Save image path to attachments array
        setUploadedImagePaths((prev) => [...prev, imagePath])

        // Insert image into editor
        if (editorRef.current) {
          const img = document.createElement('img')
          img.src = imageUrl
          img.alt = file.name
          img.loading = 'lazy'
          img.setAttribute('data-image-path', imagePath) // Store path for reference
          img.className = 'comment-image'
          img.style.maxWidth = '100%'
          img.style.maxHeight = '400px'
          img.style.height = 'auto'
          img.style.width = 'auto'
          img.style.objectFit = 'contain'
          img.style.borderRadius = '4px'
          img.style.margin = '4px 0'
          img.style.display = 'block'

          // Insert image at cursor position or at the end
          const selection = window.getSelection()
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            // If there's text selected, insert after it
            if (!range.collapsed) {
              range.collapse(false) // Collapse to end
            }
            range.insertNode(img)
            // Add a line break after image for better text editing
            const br = document.createElement('br')
            range.setStartAfter(img)
            range.insertNode(br)
            range.setStartAfter(br)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
          } else if (editorRef.current) {
            // If no selection, append to end
            editorRef.current.appendChild(img)
            const br = document.createElement('br')
            editorRef.current.appendChild(br)
            // Move cursor after the break
            const range = document.createRange()
            range.setStartAfter(br)
            range.collapse(true)
            const sel = window.getSelection()
            sel?.removeAllRanges()
            sel?.addRange(range)
          }

          onChange(editorRef.current.innerHTML)
        }

        setUploadingImages((prev) => prev.filter((name) => name !== file.name))
      } catch (error: any) {
        console.error('Error uploading image:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao fazer upload da imagem'
        alert(errorMessage)
        setUploadingImages((prev) => prev.filter((name) => name !== file.name))
        // Remove preview on error
        setImagePreviews((prev) => prev.filter((_, idx) => idx !== prev.length - 1))
      }
    }

    // Reset input
    e.target.value = ''
  }

  // Sync value with editor content (only when value changes externally, not during typing)
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      // Only update if the content is significantly different (external change)
      // This prevents overwriting content while user is typing
      const currentContent = editorRef.current.innerHTML
      const normalizedCurrent = currentContent.replace(/\s+/g, ' ').trim()
      const normalizedValue = (value || '').replace(/\s+/g, ' ').trim()
      
      // Only update if content is truly different (not just whitespace)
      if (normalizedCurrent !== normalizedValue && value !== '') {
        editorRef.current.innerHTML = value || ''
      } else if (value === '' && currentContent !== '') {
        // Clear editor only when value is explicitly empty
        editorRef.current.innerHTML = ''
      }
    }
  }, [value])
  
  // Clear previews when value is cleared (after submit)
  useEffect(() => {
    if (!value || value.trim() === '' || value === '<p></p>' || value === '<p><br></p>') {
      setImagePreviews([])
      setUploadedImagePaths([])
      if (onAttachmentsChange) {
        onAttachmentsChange([])
      }
    }
  }, [value, onAttachmentsChange])
  
  // Notify parent when attachments change (after state update)
  useEffect(() => {
    if (onAttachmentsChange && uploadedImagePaths.length > 0) {
      onAttachmentsChange(uploadedImagePaths)
    }
  }, [uploadedImagePaths, onAttachmentsChange])

  return (
    <div className="rich-comment-editor">
      <div 
        ref={toolbarRef}
        className="d-flex align-items-center gap-2 p-2 bg-light rounded-top border-bottom"
        onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking toolbar
      >
          <span className="toolbar-hint">Digite @ para mencionar usuários</span>
          <div className="vr"></div>
          <button
            type="button"
            className="btn btn-sm btn-icon btn-light"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              formatText('bold')
              editorRef.current?.focus()
            }}
            title="Negrito (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className="btn btn-sm btn-icon btn-light"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              formatText('italic')
              editorRef.current?.focus()
            }}
            title="Itálico (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 100 }}
            overlay={<Tooltip id="tooltip-link">Inserir link - Adicione um link ao texto selecionado</Tooltip>}
          >
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                openLinkModal()
              }}
            >
              <KTSVG path="/media/icons/duotune/general/gen057.svg" className="svg-icon-4" />
            </button>
          </OverlayTrigger>
          <div className="vr"></div>
          <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 100 }}
            overlay={
              <Tooltip id="tooltip-image">
                <div className="comment-tooltip-content">
                  <span className="comment-tooltip-title">Anexar imagem</span>
                  <span className="comment-tooltip-text">
                    Formatos permitidos: JPG, PNG, GIF, WEBP, SVG (máx. 3MB)
                  </span>
                  <span className="comment-tooltip-warning">
                    Para PDF e outros arquivos, use a aba "Anexos"
                  </span>
                </div>
              </Tooltip>
            }
          >
            <label 
              className="btn btn-sm btn-icon btn-light" 
              onMouseDown={(e) => e.preventDefault()}
            >
              <KTSVG path="/media/icons/duotune/files/fil021.svg" className="svg-icon-4" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleImageUpload(e)
                  editorRef.current?.focus()
                }}
                className="d-none"
                disabled={disabled || uploadingImages.length > 0}
              />
            </label>
          </OverlayTrigger>
          {uploadingImages.length > 0 && (
            <span className="text-muted small">Enviando {uploadingImages.length} imagem(ns)...</span>
          )}
        </div>

      <div className="position-relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          className={clsx(
            'form-control',
            'text-gray-900',
            'form-control-solid',
            'rich-editor-content',
            isFocused && 'focus'
          )}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true)
            setShowToolbar(true)
          }}
          onBlur={(e) => {
            // Check if focus is moving to toolbar or its children
            const relatedTarget = e.relatedTarget as HTMLElement
            if (toolbarRef.current && toolbarRef.current.contains(relatedTarget)) {
              // Focus is moving to toolbar, keep it visible
              return
            }
            
            setIsFocused(false)
            // Delay hiding toolbar to allow button clicks
            setTimeout(() => {
              // Double check if editor or toolbar has focus
              if (document.activeElement !== editorRef.current && 
                  !(toolbarRef.current && toolbarRef.current.contains(document.activeElement))) {
                setShowToolbar(false)
              }
            }, 200)
          }}
          data-placeholder={placeholder}
        />

        {showMentionSuggestions && (
          <div
            className="mention-suggestions bg-light rounded shadow-lg p-2 position-absolute"
            style={{
              top: `${mentionPosition.top}px`,
              left: `${mentionPosition.left}px`,
              zIndex: 1000,
              minWidth: '200px',
              maxHeight: '250px',
              overflowY: 'auto',
            }}
            onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking suggestions
          >
            {mentionLoading ? (
              <div className="p-2 text-muted small">Buscando usuários...</div>
            ) : mentionUsers.length > 0 ? (
              mentionUsers.map((user, index) => (
                <div
                  key={user.id}
                  className={clsx(
                    'mention-item p-2 rounded cursor-pointer d-flex align-items-center',
                    index === selectedMentionIndex && 'bg-primary text-white'
                  )}
                  onClick={() => insertMention(user)}
                  onMouseEnter={() => setSelectedMentionIndex(index)}
                >
                  <UserAvatar
                    name={user.name ?? ''}
                    avatar={user.avatar}
                    sizeClass='symbol-25px'
                    className='me-2'
                    imgClassName='rounded-circle object-fit-cover'
                  />
                  <strong>@{user.name}</strong>
                </div>
              ))
            ) : (
              <div className="p-2 text-muted small">Nenhum usuário encontrado</div>
            )}
          </div>
        )}
      </div>

      {imagePreviews.length > 0 && (
        <div className="image-preview-container">
          {imagePreviews.map((preview, index) => (
            <div key={index} className="image-preview-item">
              <img
                src={preview}
                alt={`Preview ${index}`}
                className="rounded"
              />
            </div>
          ))}
        </div>
      )}

      {/* Link Insert Modal */}
      <Modal
        show={showLinkModal}
        onHide={closeLinkModal}
        centered
        dialogClassName="mw-500px link-modal"
      >
        <div className="modal-header">
          <h2 className="fw-bolder">Inserir Link</h2>
          <div
            className="btn btn-icon btn-sm btn-active-icon-primary"
            onClick={closeLinkModal}
          >
            <KTSVG
              path="/media/icons/duotune/arrows/arr061.svg"
              className="svg-icon-1"
            />
          </div>
        </div>
        <div className="modal-body scroll-y mx-5 mx-xl-15 my-7">
          <div className="mb-5">
            <label className="form-label fw-bold">
              URL do Link <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control form-control-solid"
              placeholder="https://exemplo.com ou exemplo.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  insertLinkFromModal()
                }
              }}
              autoFocus
            />
            <div className="form-text">
              O link será aberto em uma nova aba automaticamente
            </div>
          </div>
          <div className="mb-5">
            <label className="form-label fw-bold">
              Texto do Link (opcional)
            </label>
            <input
              type="text"
              className="form-control form-control-solid"
              placeholder="Texto que será exibido (deixe vazio para usar a URL)"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  insertLinkFromModal()
                }
              }}
            />
            <div className="form-text">
              Se houver texto selecionado, ele será usado automaticamente
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={closeLinkModal}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={insertLinkFromModal}
            disabled={!linkUrl.trim()}
          >
            Inserir Link
          </button>
        </div>
      </Modal>
    </div>
  )
}
